import { useQueries, useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  fetchAuditLogDetail,
  fetchAuditLogFilters,
  fetchAuditLogStats,
  fetchAuditLogs,
  type AuditLogListParams,
} from "../api/auditLogs";

/** Колко често се презарежда списъкът в режим „На живо". */
export const AUDIT_LIVE_INTERVAL_MS = 10_000;

export function useAuditLogs(params: AuditLogListParams, live = false) {
  return useQuery({
    queryKey: ["audit-logs", params],
    queryFn: () => fetchAuditLogs(params),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchInterval: live ? AUDIT_LIVE_INTERVAL_MS : false,
  });
}

/**
 * Обобщението за периода. Върви с ТОЧНО същите филтри като списъка (без
 * страниране и подредба) — иначе кутийките отгоре броят друг набор.
 */
export function useAuditLogStats(params: AuditLogListParams, live = false) {
  return useQuery({
    queryKey: ["audit-logs", "stats", params],
    queryFn: () => fetchAuditLogStats(params),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchInterval: live ? AUDIT_LIVE_INTERVAL_MS : false,
  });
}

/** Опциите на падащите менюта — стеснени до избрания период. */
export function useAuditLogFilters(
  enabled: boolean,
  range: { start_date?: string; end_date?: string },
) {
  return useQuery({
    queryKey: ["audit-logs", "filters", range],
    queryFn: () => fetchAuditLogFilters(range),
    enabled,
    staleTime: 60_000,
  });
}

export function useAuditLogDetail(id: number | null) {
  return useQuery({
    queryKey: ["audit-logs", "detail", id],
    queryFn: () => fetchAuditLogDetail(id as number),
    enabled: id !== null && id !== undefined,
    staleTime: 5 * 60_000,
  });
}

// Всички операции по една сметка, подредени по време (възходящо) — за
// хронологичния изглед. Само мутации (без GET) ни интересуват тук.
export function useBillTimeline(billId: number | null | undefined) {
  return useQuery({
    queryKey: ["audit-logs", "bill-timeline", billId],
    queryFn: () =>
      fetchAuditLogs({
        bill_id: billId as number,
        sort_by: "timestamp",
        sort_order: "asc",
        page_size: 100,
      }),
    enabled: billId !== null && billId !== undefined,
    staleTime: 30_000,
  });
}

// Детайлите (с payload-ите) за списък от записи — споделя кеша с
// useAuditLogDetail, така че активният запис не се тегли повторно.
// Ползва се от хронологията за реконструкция на салдото след всяка операция.
export function useBillTimelineDetails(ids: number[], enabled: boolean) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ["audit-logs", "detail", id],
      queryFn: () => fetchAuditLogDetail(id),
      enabled,
      staleTime: 5 * 60_000,
    })),
  });
}
